import * as pdfjsLib from "pdfjs-dist/legacy/build/pdf";
import { PDFDocument, rgb, StandardFonts, degrees, LineCapStyle } from "pdf-lib";
import fontkit from "@pdf-lib/fontkit";

pdfjsLib.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.js";

// Font families shared with the editor UI. Standard 14 PDF fonts (no embed)
// + two embedded families (Playfair Display, Nunito).
const STD_FONTS = {
  sans: { normal: StandardFonts.Helvetica, bold: StandardFonts.HelveticaBold, italic: StandardFonts.HelveticaOblique, bolditalic: StandardFonts.HelveticaBoldOblique },
  serif: { normal: StandardFonts.TimesRoman, bold: StandardFonts.TimesRomanBold, italic: StandardFonts.TimesRomanItalic, bolditalic: StandardFonts.TimesRomanBoldItalic },
  mono: { normal: StandardFonts.Courier, bold: StandardFonts.CourierBold, italic: StandardFonts.CourierOblique, bolditalic: StandardFonts.CourierBoldOblique },
};
const CUSTOM_FONTS = {
  elegant: { normal: "/fonts/PlayfairDisplay-Regular.ttf", bold: "/fonts/PlayfairDisplay-Bold.ttf", italic: "/fonts/PlayfairDisplay-Italic.ttf", bolditalic: "/fonts/PlayfairDisplay-BoldItalic.ttf" },
  rounded: { normal: "/fonts/Nunito-Regular.ttf", bold: "/fonts/Nunito-Bold.ttf", italic: "/fonts/Nunito-Italic.ttf", bolditalic: "/fonts/Nunito-BoldItalic.ttf" },
};
const styleKey = (bold, italic) => (bold && italic ? "bolditalic" : bold ? "bold" : italic ? "italic" : "normal");

// Font choices for the editor dropdown. `css` is used for the on-screen preview.
export const FONT_OPTIONS = [
  { key: "sans", label: "Sans-serif", css: "Helvetica, Arial, sans-serif" },
  { key: "serif", label: "Serif", css: "'Times New Roman', Times, serif" },
  { key: "mono", label: "Monospace", css: "'Courier New', Courier, monospace" },
  { key: "elegant", label: "Elegante", css: "'Playfair Display', Georgia, serif" },
  { key: "rounded", label: "Arrotondato", css: "'Nunito', 'Segoe UI', sans-serif" },
];
export const fontCss = (key) => (FONT_OPTIONS.find((f) => f.key === key) || FONT_OPTIONS[0]).css;

let _uid = 0;
export const uid = (p = "id") => `${p}-${Date.now()}-${_uid++}`;

export async function fileToUint8(file) {
  const buf = await file.arrayBuffer();
  return new Uint8Array(buf);
}

export async function loadPdfjsDoc(bytes) {
  // pdfjs transfers/detaches the buffer, so hand it a copy
  const copy = bytes.slice(0);
  return pdfjsLib.getDocument({ data: copy }).promise;
}

export async function renderPage(pdfjsDoc, pageIndex, scale = 1.5, extraRotation = 0) {
  const page = await pdfjsDoc.getPage(pageIndex + 1);
  const total = (((page.rotate || 0) + extraRotation) % 360 + 360) % 360;
  const viewport = page.getViewport({ scale, rotation: total });
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  canvas.width = Math.floor(viewport.width);
  canvas.height = Math.floor(viewport.height);
  await page.render({ canvasContext: ctx, viewport }).promise;
  const dataUrl = canvas.toDataURL("image/jpeg", 0.85);
  const unscaled = page.getViewport({ scale: 1, rotation: total });
  return { dataUrl, width: canvas.width, height: canvas.height, wPt: unscaled.width, hPt: unscaled.height };
}

function hexToRgb(hex) {
  const h = (hex || "#000000").replace("#", "");
  const n = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  const int = parseInt(n, 16);
  return [(int >> 16) & 255, (int >> 8) & 255, int & 255];
}

function dataUrlToBytes(dataUrl) {
  const b64 = dataUrl.split(",")[1];
  const bin = atob(b64);
  const arr = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
  return arr;
}

// pages: [{ id, sourceId, pageIndex, rotate }]
// sources: { [sourceId]: { bytes: Uint8Array } }
// annotations: { [pageId]: [items] }  text item: { type:'text', xFrac,yFrac, fontPt, color, text, bold, italic, underline, fontFamily }
// brushes: { [pageId]: [{ color, wFrac, pts:[{x,y}] }] }
export async function exportEditedPdf({ pages, sources, annotations, brushes = {}, onProgress }) {
  const out = await PDFDocument.create();
  out.registerFontkit(fontkit);
  const cache = {};
  const getSrc = async (sid) => {
    if (!cache[sid]) cache[sid] = await PDFDocument.load(sources[sid].bytes, { ignoreEncryption: true });
    return cache[sid];
  };

  const fontCache = {};
  const getFont = async (family, bold, italic) => {
    const fam = STD_FONTS[family] ? family : CUSTOM_FONTS[family] ? family : "sans";
    const sk = styleKey(bold, italic);
    const key = `${fam}:${sk}`;
    if (fontCache[key]) return fontCache[key];
    let f;
    if (STD_FONTS[fam]) {
      f = await out.embedFont(STD_FONTS[fam][sk]);
    } else {
      const url = CUSTOM_FONTS[fam][sk];
      const bytes = await fetch(url).then((r) => r.arrayBuffer());
      f = await out.embedFont(bytes, { subset: true });
    }
    fontCache[key] = f;
    return f;
  };

  for (const pg of pages) {
    const srcDoc = await getSrc(pg.sourceId);
    const [copied] = await out.copyPages(srcDoc, [pg.pageIndex]);
    out.addPage(copied);
    if (onProgress) onProgress(out.getPageCount(), pages.length);
    const page = out.getPages()[out.getPageCount() - 1];
    // Apply the user's added rotation on top of the page's intrinsic rotation
    const baseAngle = page.getRotation().angle || 0;
    const rotation = (((baseAngle + (pg.rotate || 0)) % 360) + 360) % 360;
    page.setRotation(degrees(rotation));
    // Use the visual (post-rotation) size for coordinate mapping
    const raw = page.getSize();
    const rotated = rotation === 90 || rotation === 270;
    const W = rotated ? raw.height : raw.width;
    const H = rotated ? raw.width : raw.height;

    // Map a fractional (top-left origin) point to PDF (bottom-left origin) coords
    const mapPt = (fx, fy) => {
      if (rotation === 0) return { x: fx * W, y: H - fy * H };
      if (rotation === 90) return { x: fy * H, y: fx * W };
      if (rotation === 180) return { x: W - fx * W, y: fy * H };
      return { x: raw.width - fy * H, y: H - fx * W }; // 270
    };
    const place = (fx, fy, h) => {
      if (rotation === 0) return { x: fx * W, y: H - fy * H - h, rot: 0 };
      if (rotation === 90) return { x: fy * H + h, y: fx * W, rot: 90 };
      if (rotation === 180) return { x: W - fx * W, y: fy * H + h, rot: 180 };
      return { x: raw.width - (fy * H + h), y: H - fx * W, rot: 270 }; // 270
    };

    // 1) Overlays (text + images)
    const items = annotations[pg.id] || [];
    for (const it of items) {
      try {
        if (it.type === "text") {
          const size = Math.max(1, it.fontPt || 16);
          const [r, g, b] = hexToRgb(it.color);
          const col = rgb(r / 255, g / 255, b / 255);
          const font = await getFont(it.fontFamily || "sans", it.bold, it.italic);
          const p = place(it.xFrac, it.yFrac, size);
          const text = it.text || "";
          page.drawText(text, { x: p.x, y: p.y, size, font, color: col, rotate: degrees(p.rot) });
          if (it.underline && text) {
            const theta = (p.rot * Math.PI) / 180;
            const tw = font.widthOfTextAtSize(text, size);
            const d = size * 0.13; // offset below baseline
            const ux = p.x + d * Math.sin(theta);
            const uy = p.y - d * Math.cos(theta);
            page.drawLine({
              start: { x: ux, y: uy },
              end: { x: ux + tw * Math.cos(theta), y: uy + tw * Math.sin(theta) },
              thickness: Math.max(0.5, size * 0.06),
              color: col,
            });
          }
        } else if (it.dataUrl) {
          const isPng = it.dataUrl.startsWith("data:image/png");
          const img = isPng
            ? await out.embedPng(dataUrlToBytes(it.dataUrl))
            : await out.embedJpg(dataUrlToBytes(it.dataUrl));
          const w = it.wFrac * W;
          const h = it.hFrac * H;
          const p = place(it.xFrac, it.yFrac, h);
          page.drawImage(img, { x: p.x, y: p.y, width: w, height: h, rotate: degrees(p.rot) });
        }
      } catch (e) {
        // skip a single bad overlay rather than fail the whole export
        console.warn("overlay export skipped", e);
      }
    }

    // 2) Brush strokes last, so they cover everything (whiteout on top)
    for (const st of brushes[pg.id] || []) {
      const th = Math.max(0.5, st.wFrac * H);
      const [r, g, b] = hexToRgb(st.color);
      const col = rgb(r / 255, g / 255, b / 255);
      const pts = st.pts || [];
      if (pts.length === 0) continue;
      if (pts.length === 1) {
        const m = mapPt(pts[0].x, pts[0].y);
        page.drawLine({ start: { x: m.x, y: m.y }, end: { x: m.x + 0.01, y: m.y }, thickness: th, color: col, lineCap: LineCapStyle.Round });
      } else {
        for (let i = 1; i < pts.length; i++) {
          const a = mapPt(pts[i - 1].x, pts[i - 1].y);
          const b2 = mapPt(pts[i].x, pts[i].y);
          page.drawLine({ start: { x: a.x, y: a.y }, end: { x: b2.x, y: b2.y }, thickness: th, color: col, lineCap: LineCapStyle.Round });
        }
      }
    }
  }
  return out.save();
}

// Rotate an image dataUrl by ±90° baking the rotation into the pixels (PNG out)
export async function rotateDataUrl(dataUrl, clockwise = true) {
  const img = await new Promise((res, rej) => {
    const i = new Image();
    i.onload = () => res(i);
    i.onerror = rej;
    i.src = dataUrl;
  });
  const cv = document.createElement("canvas");
  cv.width = img.naturalHeight;
  cv.height = img.naturalWidth;
  const ctx = cv.getContext("2d");
  ctx.translate(cv.width / 2, cv.height / 2);
  ctx.rotate(((clockwise ? 90 : -90) * Math.PI) / 180);
  ctx.drawImage(img, -img.naturalWidth / 2, -img.naturalHeight / 2);
  return cv.toDataURL("image/png");
}

// Magic bucket: make the clicked color transparent across the whole image (canvas pixel pass)
export async function removeColorFromDataUrl(dataUrl, fx, fy, tolerance) {
  const img = await new Promise((res, rej) => {
    const i = new Image();
    i.onload = () => res(i);
    i.onerror = rej;
    i.src = dataUrl;
  });
  const cv = document.createElement("canvas");
  cv.width = img.naturalWidth;
  cv.height = img.naturalHeight;
  const ctx = cv.getContext("2d", { willReadFrequently: true });
  ctx.drawImage(img, 0, 0);
  const data = ctx.getImageData(0, 0, cv.width, cv.height);
  const px = data.data;
  const ix = Math.min(cv.width - 1, Math.max(0, Math.round(fx * cv.width)));
  const iy = Math.min(cv.height - 1, Math.max(0, Math.round(fy * cv.height)));
  const off = (iy * cv.width + ix) * 4;
  const tr = px[off], tg = px[off + 1], tb = px[off + 2];
  const maxD = (Math.min(100, Math.max(1, tolerance)) / 100) * 441.67;
  const maxD2 = maxD * maxD;
  const chunk = 400000; // rows of pixels per tick to keep the UI responsive
  for (let start = 0; start < px.length; start += chunk * 4) {
    const end = Math.min(px.length, start + chunk * 4);
    for (let i = start; i < end; i += 4) {
      const dr = px[i] - tr, dg = px[i + 1] - tg, db = px[i + 2] - tb;
      if (dr * dr + dg * dg + db * db <= maxD2) px[i + 3] = 0;
    }
    if (end < px.length) await new Promise((r) => setTimeout(r, 0));
  }
  ctx.putImageData(data, 0, 0);
  return cv.toDataURL("image/png");
}

export function downloadBytes(bytes, filename) {
  const blob = new Blob([bytes], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
